import React, { useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import {
  ArrowLeft,
  RefreshCw,
  Briefcase,
  PenTool,
  Users,
  MapPin,
  Mail,
  Globe,
  Calendar,
  TrendingUp,
  Ruler,
  Package,
  Clock,
  BarChart3,
  Filter,
  Sparkles,
  Activity,
  PieChart,
  ClipboardList,
  Layers,
  Building2,
  ArrowUpDown,
  ChevronDown,
  ChevronUp,
} from 'lucide-react'
import { EntityEditForm } from '@/components/EntityEditForm'

type EntityType = 'developer' | 'architect' | 'broker'

interface EntityDashboardConfig {
  apiPath: string
  label: string
  pluralLabel: string
  icon: React.ComponentType<{ className?: string }>
  accentColor: string
}

const ENTITY_CONFIG: Record<EntityType, EntityDashboardConfig> = {
  developer: {
    apiPath: 'developers',
    label: 'Developer',
    pluralLabel: 'Developers',
    icon: Briefcase,
    accentColor: 'bg-primary-600',
  },
  architect: {
    apiPath: 'architects',
    label: 'Architect',
    pluralLabel: 'Architects',
    icon: PenTool,
    accentColor: 'bg-purple-600',
  },
  broker: {
    apiPath: 'brokers',
    label: 'Broker',
    pluralLabel: 'Brokers',
    icon: Users,
    accentColor: 'bg-green-600',
  },
}

interface EntityOverviewSummary {
  entity: {
    id: number
    name: string
    externalId: string | null
    address: string | null
    email: string | null
    website: string | null
    createdAt: string | null
    type: EntityType
    label: string
  }
  metrics: {
    totals: {
      totalProjects: number
      activeProjects: number
      projectsWithAvailableUnits: number
      totalUnits: number
      availableUnits: number
      soldUnits: number
      reservedUnits: number
      unknownUnits: number
    }
    areas: {
      totalPrivateArea: number
      totalTotalArea: number
      availablePrivateArea: number
      availableTotalArea: number
    }
    pricing: {
      minPrice: number | null
      maxPrice: number | null
      avgPrice: number | null
      minSqmPrice: number | null
      maxSqmPrice: number | null
      avgSqmPrice: number | null
    }
    velocity: {
      avgDaysToSell: number | null
      avgMonthsToSell: number | null
      avgDaysOnMarket: number | null
      avgMonthsOnMarket: number | null
      lastSaleDate: string | null
      lastUnitListingDate: string | null
      lastProjectListingDate: string | null
      lastProjectCreatedAt: string | null
      lastProjectUpdatedAt: string | null
      lastScrapedAt: string | null
    }
    parking: {
      unitsWithParking: number
      parkingCoverage: number
      avgParkingSpaces: number | null
    }
    conversionRate: number
  }
}

interface EntityTypologyRow {
  typology: string
  nrUnits: number
  availableUnits: number
  soldUnits: number
  totalPrivateArea: number
  availablePrivateArea: number
  avgArea: number
  minArea: number
  maxArea: number
  avgPricePerSqm: number
  avgPriceUnit: number
  minPriceUnit: number
  maxPriceUnit: number
}

interface EntityProjectsRow {
  projectId: number
  name: string
  status: string | null
  city: string | null
  district: string | null
  area: string | null
  neighbourhood: string | null
  listingDate: string | null
  createdAt: string | null
  updatedAt: string | null
  soldOut: boolean
  soldOutDate: string | null
  hasParking: boolean
  hasPool: boolean
  hasGym: boolean
  scrapStatus: string | null
  lastScrapedAt: string | null
  totalUnits: number
  availableUnits: number
  soldUnits: number
  reservedUnits: number
  totalPrivateArea: number
  availablePrivateArea: number
  avgPrice: number
  minPrice: number
  maxPrice: number
  avgSqmPrice: number
  minSqmPrice: number
  maxSqmPrice: number
  lastSaleDate: string | null
  lastUnitListingDate: string | null
  firstUnitListingDate: string | null
}

interface EntityDashboardResponse {
  overview: EntityOverviewSummary
  typology: {
    rows: EntityTypologyRow[]
    total: EntityTypologyRow | null
  }
  projects: EntityProjectsRow[]
}

type ProjectFilter = 'all' | 'active' | 'available' | 'recentSales'

type ProjectSortKey = 'project' | 'status' | 'units' | 'availableArea' | 'avgPrice' | 'lastSale' | 'launch'

interface ProjectSortState {
  column: ProjectSortKey
  direction: 'asc' | 'desc'
}

const currencyFormatter = new Intl.NumberFormat('pt-PT', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
})

const numberFormatter = (decimals = 0) =>
  new Intl.NumberFormat('pt-PT', {
    maximumFractionDigits: decimals,
    minimumFractionDigits: 0,
  })

const formatCurrency = (value: number | null | undefined) =>
  value === null || value === undefined || Number.isNaN(value) ? '—' : currencyFormatter.format(value)

const formatNumber = (value: number | null | undefined, decimals = 0) =>
  value === null || value === undefined || Number.isNaN(value)
    ? '—'
    : numberFormatter(decimals).format(value)

const formatArea = (value: number | null | undefined) => {
  const formatted = formatNumber(value, 0)
  return formatted === '—' ? '—' : `${formatted} m²`
}

const formatPercentage = (value: number | null | undefined, decimals = 1) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—'
  return `${formatNumber(value, decimals)}%`
}

const formatDate = (value: string | null | undefined) => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString('pt-PT', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

const toTimestamp = (value: string | null | undefined) => {
  if (!value) return Number.NEGATIVE_INFINITY
  const date = new Date(value)
  const time = date.getTime()
  return Number.isNaN(time) ? Number.NEGATIVE_INFINITY : time
}

const compareProjects = (a: EntityProjectsRow, b: EntityProjectsRow, sort: ProjectSortState) => {
  const multiplier = sort.direction === 'asc' ? 1 : -1

  const compareNumeric = (aValue: number | null | undefined, bValue: number | null | undefined) => {
    const safeA = Number.isFinite(aValue) ? (aValue as number) : Number.NEGATIVE_INFINITY
    const safeB = Number.isFinite(bValue) ? (bValue as number) : Number.NEGATIVE_INFINITY
    if (safeA === safeB) return 0
    return safeA > safeB ? multiplier : -multiplier
  }

  const compareString = (aValue: string | null | undefined, bValue: string | null | undefined) => {
    const result = (aValue ?? '').localeCompare(bValue ?? '', 'pt-PT', { sensitivity: 'base' })
    if (result === 0) return 0
    return result * multiplier
  }

  switch (sort.column) {
    case 'project':
      return compareString(a.name, b.name) || a.name.localeCompare(b.name, 'pt-PT', { sensitivity: 'base' })
    case 'status':
      return compareString(a.status, b.status) || a.name.localeCompare(b.name, 'pt-PT', { sensitivity: 'base' })
    case 'units':
      return compareNumeric(a.totalUnits, b.totalUnits) || a.name.localeCompare(b.name, 'pt-PT', { sensitivity: 'base' }) * multiplier
    case 'availableArea':
      return compareNumeric(a.availablePrivateArea, b.availablePrivateArea) || a.name.localeCompare(b.name, 'pt-PT', { sensitivity: 'base' }) * multiplier
    case 'avgPrice':
      return compareNumeric(a.avgPrice, b.avgPrice) || a.name.localeCompare(b.name, 'pt-PT', { sensitivity: 'base' }) * multiplier
    case 'lastSale': {
      const result = compareNumeric(toTimestamp(a.lastSaleDate), toTimestamp(b.lastSaleDate))
      if (result !== 0) return result
      break
    }
    case 'launch': {
      const launchA = Math.max(toTimestamp(a.listingDate), toTimestamp(a.firstUnitListingDate), toTimestamp(a.createdAt))
      const launchB = Math.max(toTimestamp(b.listingDate), toTimestamp(b.firstUnitListingDate), toTimestamp(b.createdAt))
      const result = compareNumeric(launchA, launchB)
      if (result !== 0) return result
      break
    }
    default:
      break
  }

  return a.name.localeCompare(b.name, 'pt-PT', { sensitivity: 'base' })
}

const StatCard = ({
  icon: Icon,
  title,
  value,
  subtitle,
  accent = 'blue',
}: {
  icon: React.ComponentType<{ className?: string }>
  title: string
  value: string | number
  subtitle?: string
  accent?: 'blue' | 'green' | 'purple' | 'yellow' | 'red' | 'indigo'
}) => {
  const colorMap: Record<string, string> = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    yellow: 'bg-yellow-100 text-yellow-600',
    red: 'bg-red-100 text-red-600',
    indigo: 'bg-indigo-100 text-indigo-600',
  }

  return (
    <div className="bg-white rounded-xl border border-secondary-100 shadow-tagus-sm p-5">
      <div className="flex items-start justify-between">
        <div className={`p-2.5 rounded-lg ${colorMap[accent]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="text-right">
          <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500">{title}</p>
          <p className="text-2xl font-bold text-secondary-900">{value}</p>
          {subtitle && <p className="text-xs text-secondary-500 mt-1">{subtitle}</p>}
        </div>
      </div>
    </div>
  )
}

const CompactBadge = ({ label, value }: { label: string; value: string | number }) => (
  <div className="flex-1 min-w-[120px] bg-secondary-50 border border-secondary-100 rounded-lg px-4 py-3">
    <p className="text-xs uppercase tracking-wide text-secondary-500 font-semibold">{label}</p>
    <p className="text-lg font-semibold text-secondary-900 mt-1">{value}</p>
  </div>
)

const ProjectFilterPill = ({
  isActive,
  label,
  onClick,
}: {
  isActive: boolean
  label: string
  onClick: () => void
}) => (
  <button
    onClick={onClick}
    className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
      isActive
        ? 'border-primary-500 bg-primary-50 text-primary-700 shadow-sm'
        : 'border-secondary-200 text-secondary-600 hover:bg-secondary-50'
    }`}
  >
    <Filter className="h-3.5 w-3.5 mr-2" />
    {label}
  </button>
)

interface EntityDetailViewProps {
  entityType: EntityType
}

export function EntityDetailView({ entityType }: EntityDetailViewProps) {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'overview' | 'projects' | 'statistics'>('overview')
  const [projectFilter, setProjectFilter] = useState<ProjectFilter>('all')
  const [projectSort, setProjectSort] = useState<ProjectSortState>({ column: 'launch', direction: 'desc' })
  const [isEditModalOpen, setEditModalOpen] = useState(false)

  const config = ENTITY_CONFIG[entityType]

  const {
    data,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useQuery<EntityDashboardResponse>({
    queryKey: ['entity-dashboard', entityType, id],
    enabled: Boolean(id),
    queryFn: async () => {
      const response = await api.get(`/${config.apiPath}/${id}/dashboard`)
      return response.data as EntityDashboardResponse
    },
  })

  const projects = data?.projects ?? []

  const filteredProjects = useMemo(() => {
    if (!projects.length) return []

    const base = [...projects]

    switch (projectFilter) {
      case 'active':
        return base.filter((project) => {
          const status = project.status?.toLowerCase() ?? ''
          return status === '' || !['sold', 'sold_out', 'completed', 'inactive'].includes(status)
        })
      case 'available':
        return base.filter((project) => project.availableUnits > 0)
      case 'recentSales':
        return base.filter((project) => project.lastSaleDate)
      default:
        return base
    }
  }, [projects, projectFilter])

  const sortedProjects = useMemo(() => {
    if (!filteredProjects.length) return []
    return [...filteredProjects].sort((a, b) => compareProjects(a, b, projectSort))
  }, [filteredProjects, projectSort])

  const conversionRateValue = useMemo(() => {
    const totals = data?.overview?.metrics?.totals
    const totalUnits = Number(totals?.totalUnits ?? 0)
    if (!totals || totalUnits <= 0) return null

    const soldUnits = Number(totals.soldUnits ?? 0)
    const reservedUnits = Number(totals.reservedUnits ?? 0)
    const rate = ((soldUnits + reservedUnits) / totalUnits) * 100

    return Number.isFinite(rate) ? Math.round(rate) : null
  }, [
    data?.overview?.metrics?.totals?.totalUnits,
    data?.overview?.metrics?.totals?.soldUnits,
    data?.overview?.metrics?.totals?.reservedUnits,
  ])

  const IconComponent = config.icon

  const handleNavigateBack = () => navigate(`/${config.pluralLabel.toLowerCase()}`)

  const handleNavigateProject = (projectId: number) => navigate(`/projects/${projectId}`)

  const handleProjectFilterChange = (value: ProjectFilter) => {
    setProjectFilter(value)
    setProjectSort((prev) => {
      if (value === 'recentSales') {
        return { column: 'lastSale', direction: 'desc' }
      }
      return prev
    })
  }

  const handleProjectSortChange = (column: ProjectSortKey) => {
    setProjectSort((prev) => {
      if (prev.column === column) {
        return { column, direction: prev.direction === 'desc' ? 'asc' : 'desc' }
      }
      return { column, direction: 'desc' }
    })
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-secondary-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="h-10 w-10 rounded-full border-2 border-primary-200 border-t-primary-600 animate-spin" />
          <p className="text-secondary-600 text-sm">Loading {config.label.toLowerCase()} dashboard…</p>
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-secondary-50 flex items-center justify-center">
        <div className="bg-white shadow-tagus rounded-xl p-8 max-w-md text-center">
          <BarChart3 className="h-10 w-10 text-danger-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-secondary-900 mb-2">{config.label} dashboard unavailable</h2>
          <p className="text-secondary-600 text-sm mb-6">
            We couldn’t load this {config.label.toLowerCase()} analytics snapshot. Please verify the record exists or try again.
          </p>
          <div className="flex items-center justify-center space-x-3">
            <button onClick={handleNavigateBack} className="btn-secondary">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to {config.pluralLabel}
            </button>
            <button onClick={() => refetch()} className="btn-primary">
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </button>
          </div>
        </div>
      </div>
    )
  }

  const { overview, typology } = data

  return (
    <div className="min-h-screen bg-secondary-50">

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleNavigateBack}
              className="btn-secondary flex items-center"
              aria-label={`Back to ${config.pluralLabel}`}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </button>
            <div>
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-lg ${config.accentColor.replace('bg-', 'bg-').replace('600', '100')}`}>
                  <IconComponent className={`h-6 w-6 ${config.accentColor.replace('bg-', 'text-')}`} />
                </div>
                <h1 className="text-2xl font-bold text-secondary-900">{overview.entity.name}</h1>
              </div>
              <p className="text-secondary-500 mt-1 ml-14 text-sm">
                {config.label} • {overview.metrics.totals.totalProjects} projects • {overview.metrics.totals.totalUnits} units
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setEditModalOpen(true)}
              className="btn-secondary flex items-center"
            >
              <PenTool className="h-4 w-4 mr-2" />
              Edit {config.label}
            </button>
            <button
              onClick={() => refetch()}
              className="btn-primary flex items-center"
              disabled={isFetching}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-tagus border border-secondary-100">
          <div className="border-b border-secondary-100 px-6">
            <nav className="flex space-x-6" aria-label="Tabs">
              {[
                { id: 'overview', name: 'Overview', count: null },
                { id: 'projects', name: `Projects (${projects.length})`, count: projects.length },
                { id: 'statistics', name: 'Statistics', count: null },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as typeof activeTab)}
                  className={`relative py-4 text-sm font-semibold transition-colors ${
                    activeTab === tab.id
                      ? 'text-primary-600'
                      : 'text-secondary-500 hover:text-secondary-700'
                  }`}
                >
                  {tab.name}
                  {activeTab === tab.id && (
                    <span className="absolute inset-x-0 -bottom-px h-0.5 bg-primary-600 rounded-full" />
                  )}
                </button>
              ))}
            </nav>
          </div>

          <div className="px-6 py-6">
            {activeTab === 'overview' && (
              <OverviewTab overview={overview} typology={typology} projects={projects} conversionRateValue={conversionRateValue} />
            )}
            {activeTab === 'projects' && (
              <ProjectsTab
                projects={sortedProjects}
                originalCount={projects.length}
                filter={projectFilter}
                sort={projectSort}
                onFilterChange={handleProjectFilterChange}
                onSortChange={handleProjectSortChange}
                onNavigate={handleNavigateProject}
              />
            )}
            {activeTab === 'statistics' && (
              <StatisticsTab overview={overview} typology={typology} conversionRateValue={conversionRateValue} />
            )}
          </div>
        </div>
      </div>

      {isEditModalOpen && (
        <EntityEditForm
          type={entityType}
          entityId={Number(id)}
          onClose={() => setEditModalOpen(false)}
          onSuccess={() => {
            setEditModalOpen(false)
            refetch()
          }}
        />
      )}
    </div>
  )
}

function OverviewTab({
  overview,
  typology,
  projects,
  conversionRateValue,
}: {
  overview: EntityOverviewSummary
  typology: EntityDashboardResponse['typology']
  projects: EntityProjectsRow[]
  conversionRateValue: number | null
}) {
  const IconPrimary = Building2

  const primaryHighlights = [
    {
      icon: PieChart,
      title: 'Projects with stock',
      value: formatNumber(overview.metrics.totals.projectsWithAvailableUnits, 0),
      subtitle: `of ${formatNumber(overview.metrics.totals.totalProjects, 0)} projects`,
      accent: 'blue' as const,
    },
    {
      icon: TrendingUp,
      title: 'Average price / m²',
      value: formatCurrency(overview.metrics.pricing.avgSqmPrice),
      subtitle: `${formatCurrency(overview.metrics.pricing.minSqmPrice)} – ${formatCurrency(overview.metrics.pricing.maxSqmPrice)}`,
      accent: 'purple' as const,
    },
    {
      icon: Clock,
      title: 'Average days to sell',
      value: formatNumber(overview.metrics.velocity.avgDaysToSell, 0),
      subtitle: overview.metrics.velocity.avgMonthsToSell
        ? `${formatNumber(overview.metrics.velocity.avgMonthsToSell, 1)} months`
        : undefined,
      accent: 'indigo' as const,
    },
    {
      icon: Sparkles,
      title: 'Latest sale',
      value: formatDate(overview.metrics.velocity.lastSaleDate),
      accent: 'green' as const,
    },
    {
      icon: Layers,
      title: 'Available private area',
      value: formatArea(overview.metrics.areas.availablePrivateArea),
      subtitle: `${formatArea(overview.metrics.areas.totalPrivateArea)} total`,
      accent: 'yellow' as const,
    },
    {
      icon: ClipboardList,
      title: 'Reserved units',
      value: formatNumber(overview.metrics.totals.reservedUnits, 0),
      accent: 'red' as const,
    },
  ]

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            <StatCard
              icon={IconPrimary}
              title="Total Projects"
              value={overview.metrics.totals.totalProjects}
              subtitle={`${overview.metrics.totals.activeProjects} active`}
              accent="blue"
            />
            <StatCard
              icon={Package}
              title="Total Units"
              value={overview.metrics.totals.totalUnits}
              subtitle={`${overview.metrics.totals.availableUnits} available • ${overview.metrics.totals.soldUnits} sold`}
              accent="green"
            />
            <StatCard
              icon={Ruler}
              title="Private Area"
              value={formatArea(overview.metrics.areas.totalPrivateArea)}
              subtitle={`${formatArea(overview.metrics.areas.availablePrivateArea)} available`}
              accent="purple"
            />
            <StatCard
              icon={TrendingUp}
              title="Average Unit Price"
              value={formatCurrency(overview.metrics.pricing.avgPrice)}
              subtitle={`${formatCurrency(overview.metrics.pricing.minPrice)} – ${formatCurrency(overview.metrics.pricing.maxPrice)}`}
              accent="yellow"
            />
            <StatCard
              icon={Activity}
              title="Conversion Rate"
              value={formatPercentage(conversionRateValue, 0)}
              subtitle={`${(overview.metrics.totals.soldUnits ?? 0) + (overview.metrics.totals.reservedUnits ?? 0)} units sold/reserved`}
              accent="red"
            />
            <StatCard
              icon={Clock}
              title="Avg Time on Market"
              value={formatNumber(overview.metrics.velocity.avgDaysOnMarket, 0) + ' days'}
              subtitle={
                overview.metrics.velocity.avgMonthsOnMarket
                  ? `${formatNumber(overview.metrics.velocity.avgMonthsOnMarket, 1)} months`
                  : undefined
              }
              accent="indigo"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {primaryHighlights.map((item) => (
              <StatCard
                key={item.title}
                icon={item.icon}
                title={item.title}
                value={item.value}
                subtitle={item.subtitle}
                accent={item.accent}
              />
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="card">
              <h3 className="text-sm font-semibold text-secondary-900 mb-4">Contact &amp; Identity</h3>
              <div className="space-y-3 text-sm text-secondary-700">
                <div className="flex items-center space-x-3">
                  <Mail className="h-4 w-4 text-secondary-400" />
                  <span>{overview.entity.email ?? '—'}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Globe className="h-4 w-4 text-secondary-400" />
                  {overview.entity.website ? (
                    <a
                      href={overview.entity.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-700"
                    >
                      {overview.entity.website}
                    </a>
                  ) : (
                    <span>—</span>
                  )}
                </div>
                <div className="flex items-start space-x-3">
                  <MapPin className="h-4 w-4 text-secondary-400 mt-0.5" />
                  <span>{overview.entity.address ?? '—'}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Calendar className="h-4 w-4 text-secondary-400" />
                  <span>Created on {formatDate(overview.entity.createdAt)}</span>
                </div>
              </div>
            </div>

            <div className="card">
              <h3 className="text-sm font-semibold text-secondary-900 mb-4">Timeline &amp; Operations</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-secondary-700">
                <div>
                  <p className="text-xs uppercase text-secondary-500 font-semibold">Last project update</p>
                  <p className="mt-1 font-medium">{formatDate(overview.metrics.velocity.lastProjectUpdatedAt)}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-secondary-500 font-semibold">Last scraped</p>
                  <p className="mt-1 font-medium">{formatDate(overview.metrics.velocity.lastScrapedAt)}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-secondary-500 font-semibold">First active unit</p>
                  <p className="mt-1 font-medium">
                    {formatDate(typology.total ? overview.metrics.velocity.lastProjectCreatedAt : null)}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase text-secondary-500 font-semibold">Units with parking</p>
                  <p className="mt-1 font-medium">
                    {overview.metrics.parking.unitsWithParking}{' '}
                    <span className="text-secondary-500">
                      ({formatPercentage(overview.metrics.parking.parkingCoverage)})
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-4 space-y-6">
          <div className="card">
            <h3 className="text-sm font-semibold text-secondary-900 mb-4">Portfolio snapshot</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-secondary-500">Projects</span>
                <span className="font-semibold text-secondary-900">{overview.metrics.totals.totalProjects}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-secondary-500">Active projects</span>
                <span className="font-semibold text-secondary-900">{overview.metrics.totals.activeProjects}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-secondary-500">Projects with stock</span>
                <span className="font-semibold text-secondary-900">
                  {overview.metrics.totals.projectsWithAvailableUnits}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-secondary-500">Total available m²</span>
                <span className="font-semibold text-secondary-900">
                  {formatNumber(overview.metrics.areas.availablePrivateArea, 0)}
                </span>
              </div>
            </div>
          </div>

          <div className="card">
            <h3 className="text-sm font-semibold text-secondary-900 mb-4">Typology mix</h3>
            {typology.rows.length ? (
              <div className="space-y-3 text-sm">
                {typology.rows.slice(0, 4).map((row) => (
                  <div key={row.typology} className="flex items-center justify-between">
                    <span className="text-secondary-600">{row.typology}</span>
                    <span className="font-medium text-secondary-900">
                      {row.nrUnits} units • {formatPercentage((row.availableUnits / Math.max(row.nrUnits, 1)) * 100, 0)} available
                    </span>
                  </div>
                ))}
                {typology.rows.length > 4 && (
                  <p className="text-xs text-secondary-500">
                    +{typology.rows.length - 4} more typologies available in statistics
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm text-secondary-500">No unit typology data available yet.</p>
            )}
          </div>

        </div>
      </div>
    </div>
  )
}

function ProjectsTab({
  projects,
  originalCount,
  filter,
  sort,
  onFilterChange,
  onSortChange,
  onNavigate,
}: {
  projects: EntityProjectsRow[]
  originalCount: number
  filter: ProjectFilter
  sort: ProjectSortState
  onFilterChange: (value: ProjectFilter) => void
  onSortChange: (column: ProjectSortKey) => void
  onNavigate: (projectId: number) => void
}) {
  const filters: { value: ProjectFilter; label: string }[] = [
    { value: 'all', label: 'All projects' },
    { value: 'active', label: 'Active' },
    { value: 'available', label: 'With availability' },
    { value: 'recentSales', label: 'Last units sold' },
  ]

  const SortableHeader = ({
    label,
    column,
    align = 'left',
  }: {
    label: string
    column: ProjectSortKey
    align?: 'left' | 'right'
  }) => {
    const isActive = sort.column === column
    const IconComponent = isActive ? (sort.direction === 'desc' ? ChevronDown : ChevronUp) : ArrowUpDown

    return (
      <button
        type="button"
        onClick={() => onSortChange(column)}
        className={`flex w-full items-center gap-1 text-xs font-semibold uppercase transition-colors ${
          align === 'right' ? 'justify-end text-right' : 'justify-start text-left'
        } ${isActive ? 'text-primary-600' : 'text-secondary-500 hover:text-secondary-700'}`}
      >
        <span>{label}</span>
        <IconComponent className="h-3.5 w-3.5" />
      </button>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {filters.map((item) => (
            <ProjectFilterPill
              key={item.value}
              isActive={filter === item.value}
              label={item.label}
              onClick={() => onFilterChange(item.value)}
            />
          ))}
        </div>
        <p className="text-sm text-secondary-500">
          Showing{' '}
          <span className="font-semibold text-secondary-900">{projects.length}</span> of{' '}
          <span className="font-semibold text-secondary-900">{originalCount}</span> projects
        </p>
      </div>

      <div className="border border-secondary-100 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-secondary-100">
            <thead className="bg-secondary-50 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3">
                  <SortableHeader label="Project" column="project" />
                </th>
                <th className="px-4 py-3">
                  <SortableHeader label="Status" column="status" />
                </th>
                <th className="px-4 py-3">
                  <SortableHeader label="Units" column="units" align="right" />
                </th>
                <th className="px-4 py-3">
                  <SortableHeader label="Available m²" column="availableArea" align="right" />
                </th>
                <th className="px-4 py-3">
                  <SortableHeader label="Avg price" column="avgPrice" align="right" />
                </th>
                <th className="px-4 py-3">
                  <SortableHeader label="Last sale" column="lastSale" />
                </th>
                <th className="px-4 py-3">
                  <SortableHeader label="Launch" column="launch" />
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-secondary-100">
              {projects.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-secondary-500 text-sm">
                    No projects match the selected filters yet.
                  </td>
                </tr>
              )}
              {projects.map((project) => (
                <tr
                  key={project.projectId}
                  className="hover:bg-secondary-50 cursor-pointer transition-colors"
                  onClick={() => onNavigate(project.projectId)}
                >
                  <td className="px-4 py-3">
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold text-secondary-900">{project.name}</span>
                      <span className="text-xs text-secondary-500">
                        {[project.city, project.district].filter(Boolean).join(', ') || 'Location N/A'}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-secondary-700">
                    <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700">
                      {project.status ?? 'N/A'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <p className="text-sm font-semibold text-secondary-900">{project.totalUnits}</p>
                    <p className="text-xs text-secondary-500">
                      {project.availableUnits} avail • {project.soldUnits} sold
                    </p>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <p className="text-sm font-semibold text-secondary-900">
                      {formatNumber(project.availablePrivateArea, 0)} m²
                    </p>
                    <p className="text-xs text-secondary-500">
                      of {formatNumber(project.totalPrivateArea, 0)} m²
                    </p>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <p className="text-sm font-semibold text-secondary-900">
                      {formatCurrency(project.avgPrice)}
                    </p>
                    <p className="text-xs text-secondary-500">
                      {formatCurrency(project.minPrice)} – {formatCurrency(project.maxPrice)}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-sm text-secondary-700">{formatDate(project.lastSaleDate)}</td>
                  <td className="px-4 py-3 text-sm text-secondary-700">
                    {formatDate(project.listingDate || project.firstUnitListingDate || project.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

function StatisticsTab({
  overview,
  typology,
  conversionRateValue,
}: {
  overview: EntityOverviewSummary
  typology: EntityDashboardResponse['typology']
  conversionRateValue: number | null
}) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <StatCard
          icon={Package}
          title="Total Units"
          value={overview.metrics.totals.totalUnits}
          subtitle={`${overview.metrics.totals.availableUnits} available`}
          accent="blue"
        />
        <StatCard
          icon={TrendingUp}
          title="Avg Price / Unit"
          value={formatCurrency(overview.metrics.pricing.avgPrice)}
          subtitle={`${formatCurrency(overview.metrics.pricing.minPrice)} – ${formatCurrency(overview.metrics.pricing.maxPrice)}`}
          accent="purple"
        />
        <StatCard
          icon={Ruler}
          title="Avg Price / m²"
          value={formatCurrency(overview.metrics.pricing.avgSqmPrice)}
          subtitle={`${formatCurrency(overview.metrics.pricing.minSqmPrice)} – ${formatCurrency(overview.metrics.pricing.maxSqmPrice)}`}
          accent="indigo"
        />
        <StatCard
          icon={Clock}
          title="Avg Days to Sell"
          value={formatNumber(overview.metrics.velocity.avgDaysToSell, 0)}
          subtitle={`≈ ${formatNumber(overview.metrics.velocity.avgMonthsToSell, 1)} months`}
          accent="green"
        />
        <StatCard
          icon={Activity}
          title="Conversion"
          value={formatPercentage(conversionRateValue, 0)}
          subtitle={`${(overview.metrics.totals.soldUnits ?? 0) + (overview.metrics.totals.reservedUnits ?? 0)} sold/reserved units`}
          accent="red"
        />
        <StatCard
          icon={Layers}
          title="Available Area"
          value={formatArea(overview.metrics.areas.availablePrivateArea)}
          subtitle={`${formatPercentage(
            (overview.metrics.areas.availablePrivateArea / Math.max(overview.metrics.areas.totalPrivateArea, 1)) * 100,
            0,
          )} of total private area`}
          accent="yellow"
        />
      </div>

      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-secondary-900">Typology breakdown</h3>
          <span className="text-sm text-secondary-500">
            {typology.rows.length} typologies • {overview.metrics.totals.totalUnits} units
          </span>
        </div>
        {typology.rows.length ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-secondary-100">
              <thead className="bg-secondary-50 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-secondary-500 uppercase">Typology</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-secondary-500 uppercase">Units</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-secondary-500 uppercase">Available</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-secondary-500 uppercase">Avg m²</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-secondary-500 uppercase">Avg price</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-secondary-500 uppercase">Avg €/m²</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-secondary-100">
                {typology.rows.map((row) => (
                  <tr key={row.typology} className="hover:bg-secondary-50">
                    <td className="px-3 py-2 text-sm font-semibold text-secondary-900">{row.typology}</td>
                    <td className="px-3 py-2 text-sm text-right text-secondary-700">{row.nrUnits}</td>
                    <td className="px-3 py-2 text-sm text-right text-secondary-700">
                      {row.availableUnits} ({formatPercentage((row.availableUnits / Math.max(row.nrUnits, 1)) * 100, 0)})
                    </td>
                    <td className="px-3 py-2 text-sm text-right text-secondary-700">{formatArea(row.avgArea)}</td>
                    <td className="px-3 py-2 text-sm text-right text-secondary-700">{formatCurrency(row.avgPriceUnit)}</td>
                    <td className="px-3 py-2 text-sm text-right text-secondary-700">{formatCurrency(row.avgPricePerSqm)}</td>
                  </tr>
                ))}
                {typology.total && (
                  <tr className="bg-secondary-50">
                    <td className="px-3 py-2 text-sm font-semibold text-secondary-900">TOTAL</td>
                    <td className="px-3 py-2 text-sm text-right text-secondary-900">{typology.total.nrUnits}</td>
                    <td className="px-3 py-2 text-sm text-right text-secondary-900">
                      {typology.total.availableUnits} ({formatPercentage(
                        (typology.total.availableUnits / Math.max(typology.total.nrUnits, 1)) * 100,
                        0,
                      )})
                    </td>
                    <td className="px-3 py-2 text-sm text-right text-secondary-900">{formatArea(typology.total.avgArea)}</td>
                    <td className="px-3 py-2 text-sm text-right text-secondary-900">{formatCurrency(typology.total.avgPriceUnit)}</td>
                    <td className="px-3 py-2 text-sm text-right text-secondary-900">{formatCurrency(typology.total.avgPricePerSqm)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-secondary-500">No typology data is available for this portfolio yet.</p>
        )}
      </div>
    </div>
  )
}


